import { Col, DatePicker, Form, Input, Row, Select, Switch, Table, Checkbox } from "antd";
import COLORS from "common/Colors";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { dateInDetail, getKenyanDateTime } from "utils/Date";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { ReactComponent as Trash } from "../../assets/Trash_Full.svg";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { message } from "antd";
import { SearchOutlined, FilterOutlined, EditOutlined, LoadingOutlined } from "@ant-design/icons";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";

dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

// ─────────────────────────────────────────────
// Styled Components
// ─────────────────────────────────────────────

const MyAnnouncementWrap = styled.div`
  .ant-input::placeholder,
  .ant-picker-input > input::placeholder {
    color: #9ca3af !important;
  }
  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }
  .ant-input,
  .ant-picker,
  .ant-select-selector,
  .ant-btn {
    border-radius: 24px !important;
  }
  .ant-btn {
    width: 100%;
  }
  .ant-select-selector {
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: 34px !important;
    display: flex !important;
    align-items: center !important;
  }
`;

const ActionWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  gap: 20px;
  justify-content: space-evenly;
  .ant-table-row:hover & {
    opacity: 1;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
  }
  .ant-table-container {
    border-radius: 12px;
  }
  .ant-table-content {
    overflow-x: auto;
  }
  table {
    width: 100%;
    min-width: 900px;
  }
  .ant-table-cell {
    padding: 16px;
    white-space: nowrap;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
  }
  .reason-column {
    min-width: 280px;
    white-space: normal;
  }
  .non-white {
    background: #f9f9f9;
  }
  @media (max-width: 768px) {
    .ant-table-cell {
      padding: 10px;
      font-size: 12px;
    }
  }
  .ant-table-tbody .non-white .ant-table-cell-fix-right {
    background: #f9f9f9 !important;
  }

  .ant-table-tbody tr:not(.non-white) .ant-table-cell-fix-right {
    background: #fff !important; 
  }
  .ant-table-tbody .ant-table-row:hover .ant-table-cell-fix-right,
  .ant-table-tbody .ant-table-row:hover > .ant-table-cell-fix-right {
    background: #fafafa !important;
  }
`;

// ── Custom Delete Modal ──────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px 36px 28px;
  width: 460px;
  max-width: 90vw;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
`;

const ModalText = styled.p`
  font-size: 15px;
  color: #262626;
  line-height: 1.6;
  margin: 0 0 20px;
  strong { font-weight: 700; }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
`;

const BtnNo = styled.button`
  padding: 8px 0;
  border-radius: 8px;
  border: 1.5px solid #262626;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #262626;
  width: 120px;
  &:hover { background: #f5f5f5; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BtnYes = styled.button`
  padding: 8px 0;
  border-radius: 8px;
  border: none;
  background: #b7f0d8;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #262626;
  width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #9de8cb; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const MyAnnouncement = ({ data = [], loading, onRefresh }) => {
	const [form] = Form.useForm();
	const containerRef = useRef(null);
	const history = useHistory();
	const pageTitleRef = useRef(null);

	const [scrollY, setScrollY] = useState(120);
	const [importantOnly, setImportantOnly] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [dateRange, setDateRange] = useState([null, null]);
	const [statusFilter, setStatusFilter] = useState(null);

	// ── Delete modal state ────────────────────────────────────────────────────
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [dontShowAgain, setDontShowAgain] = useState(false);

	useEffect(() => {
		if (!pageTitleRef.current) return;
		const baseOffset = 300;
		const minHeight = 120;
		const calculateScrollY = () => {
			const headerHeight = pageTitleRef.current.offsetHeight;
			const availableHeight = window.innerHeight - headerHeight - baseOffset;
			setScrollY(Math.max(minHeight, availableHeight));
		};
		calculateScrollY();
		const resizeObserver = new ResizeObserver(() => calculateScrollY());
		resizeObserver.observe(pageTitleRef.current);
		window.addEventListener("resize", calculateScrollY);
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", calculateScrollY);
		};
	}, []);

	// ── Delete handler ────────────────────────────────────────────────────────
	const handleDeleteConfirm = async () => {
		try {
			setDeleteLoading(true);
			await CALL_API(
				"insyts-user-dashboard/deleteannouncement",
				"delete",
				{ id: deleteTarget }
			);
			setDeleteConfirmOpen(false);
			message.success("Announcement deleted successfully");
			if (onRefresh) onRefresh();
		} catch {
			message.error("Failed to delete announcement");
		} finally {
			setDeleteLoading(false);
		}
	};

	const dataSource = data
		?.filter((item) => {
			const matchImportant = importantOnly ? item?.level === "Important" : true;
			const matchSearch = searchText
				? item?.title?.toLowerCase().includes(searchText.toLowerCase())
				: true;
			const matchDate = dateRange[0]
				? dayjs(item?.createdat).isSame(dayjs(dateRange[0]), "day")
				: true;
			const matchStatus = statusFilter
				? item?.announcementstatus?.toLowerCase() === statusFilter.toLowerCase()
				: true;
			return matchImportant && matchSearch && matchDate && matchStatus;
		})
		?.map((item) => ({
			key: item?.id,
			_raw: item,
			title: item?.title,
			level: item?.level,
			Status: item?.announcementstatus,
			date: (() => {
				const d = item?.updatedat || item?.createdat;
				return d ? dayjs(d).format("DD MMM YYYY; hh:mm A") : "-";
			})(),
		}));

	const columns = [
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
			ellipsis: { showTitle: true },
			width: 300,
			render: (text) => (
				<span
					style={{
						display: "block",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						maxWidth: 280,
					}}
					title={text}
				>
					{text}
				</span>
			),
		},
		{ title: "Date & Time", dataIndex: "date", key: "date" },
		{ title: "Level", dataIndex: "level", key: "level" },
		{ title: "Status", dataIndex: "Status", key: "Status" },
		{
			key: "actions",
			width: 130,
			fixed: "right",
			render: (_, record) => (
				<ActionWrapper>
					<EditOutlined
						style={{
							cursor: "pointer",
							backgroundColor: "#b7f0d8",
							padding: 4,
							borderRadius: 4,
						}}
						onClick={() =>
							history.push("/add-edit-announcement", { editData: record._raw })
						}
					/>
					<Trash
						style={{
							cursor: "pointer",
							backgroundColor: "#b7f0d8",
							padding: 4,
							borderRadius: 4,
						}}
						onClick={() => {
							setDeleteTarget(record.key);
							setDeleteConfirmOpen(true);
						}}
					/>
				</ActionWrapper>
			),
		},
	];

	return (
		<MyAnnouncementWrap>

			{/* ── Custom Delete Confirm Modal ── */}
			{deleteConfirmOpen && (
				<ModalOverlay onClick={() => !deleteLoading && setDeleteConfirmOpen(false)}>
					<ModalBox onClick={(e) => e.stopPropagation()}>
						<ModalText>
							Are you sure you want to <strong>delete</strong> this announcement
							from the list?
						</ModalText>

						<Checkbox
							checked={dontShowAgain}
							onChange={(e) => setDontShowAgain(e.target.checked)}
							style={{ fontSize: 14, color: "#262626" }}
						>
							Don't show this again
						</Checkbox>

						<ModalFooter>
							<BtnNo
								onClick={() => setDeleteConfirmOpen(false)}
								disabled={deleteLoading}
							>
								No
							</BtnNo>
							<BtnYes onClick={handleDeleteConfirm} disabled={deleteLoading}>
								{deleteLoading
									? <LoadingOutlined style={{ fontSize: 14 }} />
									: "Yes"
								}
							</BtnYes>
						</ModalFooter>
					</ModalBox>
				</ModalOverlay>
			)}

			{/* ── Filters ── */}
			<div className="page-title" ref={pageTitleRef}>
				<Form
					form={form}
					layout="vertical"
					initialValues={{
						status: "waiting",
						duration_type: 4,
						month: dateInDetail(getKenyanDateTime()).monthName,
						status_id: "4",
						start_date: dayjs(),
						end_date: dayjs(),
					}}
				>
					<Row gutter={[12, 0]} align="middle" style={{ marginBottom: 16 }}>
						{/* Search */}
						<Col>
							<Input
								prefix={<SearchOutlined style={{ color: "#9ca3af", fontSize: 13 }} />}
								placeholder="Search"
								allowClear
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								style={{ borderRadius: 999, height: 36, width: 200 }}
							/>
						</Col>

						{/* Date */}
						<Col ref={containerRef}>
							<DatePicker
								format="DD-MM-YYYY"
								allowClear
								placeholder="Date"
								getPopupContainer={() => containerRef.current}
								onChange={(date) => setDateRange(date ? [date, date] : [null, null])}
								style={{ borderRadius: 999, height: 36, width: 200 }}
							/>
						</Col>

						{/* Status */}
						<Col ref={containerRef}>
							<Select
								placeholder={
									<span style={{ display: "flex", alignItems: "center", gap: 4 }}>
										<FilterOutlined style={{ color: "#9ca3af" }} />
										Status
									</span>
								}
								allowClear
								value={statusFilter}
								onChange={(val) => setStatusFilter(val ?? null)}
								options={[
									{ label: "Published", value: "Published" },
									{ label: "Draft", value: "Draft" },
								]}
								getPopupContainer={() => containerRef.current}
								style={{ width: 200, height: 36 }}
							/>
						</Col>

						{/* Spacer */}
						<Col flex="auto" />

						{/* Important switch */}
						<Col style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
							<span style={{ fontSize: 14, color: "#374151" }}>Important</span>
							<Switch
								checked={importantOnly}
								onChange={(checked) => setImportantOnly(checked)}
								style={{ backgroundColor: importantOnly ? "#6ee7b7" : undefined }}
							/>
						</Col>

						{/* Add Announcement */}
						<Col>
							<button
								style={{
									background: "#b7f0d8",
									color: "#000000",
									border: "none",
									borderRadius: 8,
									padding: "0 16px",
									fontSize: 14,
									fontWeight: 500,
									cursor: "pointer",
									whiteSpace: "nowrap",
									height: 36,
								}}
								onClick={() => history?.push("/add-edit-announcement")}
							>
								Add Announcement
							</button>
						</Col>
					</Row>
				</Form>
			</div>

			{/* ── Table ── */}
			<StyledTable
				columns={columns}
				dataSource={dataSource}
				pagination={false}
				loading={loading}
				scroll={{ y: scrollY }}
				style={{ minHeight: scrollY + 50 }}
				rowClassName={(_, index) => (index % 2 ? "non-white" : "")}
			/>
		</MyAnnouncementWrap>
	);
};

export default MyAnnouncement;